/* eslint-disable no-restricted-properties */
import { CoreLayout } from "@/base-components/core-layout";
import { ErrorBoundary } from "@/components/error-boundary";
import { ApolloProviderWithAuth0 } from "@/providers/apollo-provider-with-auth0";
import { CompanyProvider } from "@/providers/company-provider";
import { MeProvider } from "@/providers/me-provider";
import { NavigationProvider } from "@/providers/navigation-provider";
import { ReactRouter5Adapter } from "use-query-params/adapters/react-router-5";

import "@/styles/base.css";
import "@/styles/index.css";

import { getUrls } from "@/utils/get-urls";
import { lazyWithPreload } from "@/utils/lazy-with-preload";
import { lazyWithRetry } from "@/utils/lazy-with-retry";
import { watchForNewVersion } from "@/utils/new-version";
import { shareHistoryBlocks } from "@/utils/share-history-blocks";
import {
  RouteTransitionCommit,
  withRouteTransitions,
} from "@/utils/route-transitions";
import { sentryOptions } from "@/utils/sentry-options";
import { Auth0Provider } from "@auth0/auth0-react";
import { init } from "@sentry/browser";
import React, { Suspense, useEffect, useState } from "react";
import { ToastBar, Toaster } from "react-hot-toast";

import "react-loading-skeleton/dist/skeleton.css";

import { createBrowserHistory } from "history";
import { Route, Router, Switch } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";

import { LoadingHome } from "./components/loading-home";
import { TooltipProvider } from "./components/ui/tooltip";
import { env } from "./env";
import { GeneralPrivilegeProvider } from "./providers/general-privilege-provider";
import { ThemeProvider } from "./providers/theme-provider";
import {
  opensPreloadedPage,
  preloadStartPageFromEntry,
} from "./routes/start-page-route-loader";

console.info("React version:", React.version);

const AdminRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-route").then((m) => ({ default: m.AdminRoute })),
);
const SignupRoute = lazyWithRetry(() =>
  import("@/routes/signup-route").then((m) => ({ default: m.SignupRoute })),
);
const NotAuthorized = lazyWithRetry(() =>
  import("@/routes/not-authorized").then((m) => ({ default: m.NotAuthorized })),
);
const UserNotFoundRoute = lazyWithRetry(() =>
  import("@/routes/user-not-found-route").then((m) => ({
    default: m.UserNotFoundRoute,
  })),
);
const LoginRoute = lazyWithRetry(() =>
  import("@/routes/login-route").then((m) => ({ default: m.LoginRoute })),
);
const loadAppRoute = () =>
  import("@/routes/app-route").then((m) => ({ default: m.AppRoute }));
const AppRoute = lazyWithRetry(loadAppRoute);

// The app's chunk is otherwise only requested once Auth0 has finished signing in. Starting it now lets the two
// overlap, and the lazy route reuses the request. A failure surfaces when the route itself loads.
if (
  !["/login", "/signup", "/not-authorized", "/user-not-found", "/admin"].some(
    (path) => window.location.pathname.startsWith(path),
  )
) {
  loadAppRoute().catch(() => undefined);
}
preloadStartPageFromEntry();

// React holds a Suspense reveal until 300 ms after the last fallback, so a fallback at this point keeps the page's
// queries from starting until about 350 ms, however soon `Me` answers. This draws the same loading screen without one.
// The pages this is for load their code while `Me` is on its way and never suspend. Any other page needs that fallback:
// without it, its own would come later, when `Me` answers, and the 300 ms would count from there.
const PreloadedAppRoute = lazyWithPreload(loadAppRoute);
function AppRouteWithoutFallback() {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const settle = () => setSettled(true);
    PreloadedAppRoute.preload().then(settle, settle);
  }, []);
  return settled ? <PreloadedAppRoute /> : <LoadingHome />;
}
const RootRoute = opensPreloadedPage() ? AppRouteWithoutFallback : AppRoute;

console.info("Version:", env.VITE_VERSION, getUrls().currentUrl);

if (process.env.NODE_ENV === "production") {
  init(sentryOptions(`cruitive-app@${env.VITE_VERSION}`));
}

const urls = getUrls();

const history = createBrowserHistory();
shareHistoryBlocks(history);
watchForNewVersion(history);
const routerHistory = withRouteTransitions(history);

// eslint-disable-next-line
if (typeof (window as any).global === "undefined") {
  // eslint-disable-next-line
  (window as any).global = window;
}

export function Index() {
  return (
    <Router history={routerHistory}>
      <RouteTransitionCommit />
      <ThemeProvider>
        <Auth0Provider
          clientId="ODGMaMTOt27fNG1nFDGPcySrHx37H1OC"
          domain="auth.cruitive.com"
          // A reload or new tab reuses the stored access token instead of waiting for Auth0's silent sign-in. There
          // are no refresh tokens, so nothing stored can mint new tokens; an expired token falls back to the iframe.
          cacheLocation="localstorage"
          authorizationParams={{
            redirect_uri: urls.currentUrl,
            audience: "https://server.cruitive.com",
          }}
        >
          <ApolloProviderWithAuth0>
            <>
              <QueryParamProvider adapter={ReactRouter5Adapter}>
                <CoreLayout>
                  <MeProvider>
                    <CompanyProvider>
                      <GeneralPrivilegeProvider>
                        <NavigationProvider>
                          <ErrorBoundary>
                            <TooltipProvider>
                              <Suspense fallback={<LoadingHome />}>
                                <Switch>
                                  <Route path="/login" component={LoginRoute} />
                                  <Route
                                    path="/signup"
                                    component={SignupRoute}
                                    exact={true}
                                  />
                                  <Route
                                    path="/not-authorized"
                                    component={NotAuthorized}
                                    exact={true}
                                  />
                                  <Route
                                    path="/user-not-found"
                                    component={UserNotFoundRoute}
                                    exact={true}
                                  />
                                  <Route path="/admin" component={AdminRoute} />
                                  <Route path="/" component={RootRoute} />
                                </Switch>
                              </Suspense>
                            </TooltipProvider>
                          </ErrorBoundary>
                          <Toaster
                            toastOptions={{
                              className: "toast-surface",
                              style: {
                                backgroundColor: "#374151",
                                color: "#fff",
                                padding: "0.75rem",
                                zIndex: 11000,
                                fontSize: ".875rem",
                              },
                              success: {
                                iconTheme: {
                                  primary: "#3DB6A8",
                                  secondary: "#fff",
                                },
                              },
                              error: {
                                iconTheme: {
                                  primary: "#ef4444",
                                  secondary: "#fff",
                                },
                              },
                            }}
                          >
                            {(t) => {
                              // height: measured after the first render, which
                              // is what starts the toast coming in.
                              const shown = t.visible && t.height !== undefined;
                              return (
                                <ToastBar
                                  toast={t}
                                  style={{
                                    animation: "none",
                                    opacity: shown ? 1 : 0,
                                    transform: shown
                                      ? "none"
                                      : "translateY(-50%) scale(0.97)",
                                    transitionDuration: t.visible
                                      ? undefined
                                      : "160ms",
                                  }}
                                />
                              );
                            }}
                          </Toaster>
                        </NavigationProvider>
                      </GeneralPrivilegeProvider>
                    </CompanyProvider>
                  </MeProvider>
                </CoreLayout>
              </QueryParamProvider>
            </>
          </ApolloProviderWithAuth0>
        </Auth0Provider>
      </ThemeProvider>
    </Router>
  );
}
